import { z } from 'zod';

export const RoleCode = z.enum(['admin', 'manager', 'operator']);
export type RoleCode = z.infer<typeof RoleCode>;

export const RIGHTS = [
  'orders.read',
  'orders.create',
  'orders.reject',
  'orders.confirm',
  'orders.edit-provodka',
  // Смена исполнителя в уже созданной заявке (с обязательной причиной в аудите).
  // Разрешено вниз по иерархии — admin/manager/operator (в отличие от edit-provodka).
  // Блокируется, если нога исполнителя (idx2/idx3) уже подтверждена.
  'orders.change-executor',
  // Откат подтверждения ноги (done → pending, удаление reverse_entry) — только admin.
  // Опасная операция уровня delete: разворачивает исполненное обязательство обратно.
  'orders.rollback-confirm',
  // Удаление заявки (soft-delete в статус 'deleted') — только admin.
  'orders.delete',
  'refs.read',
  'refs.write',
  // Управление контрагентами-чатами (reference.accounts, страница /admin/chats):
  // создание и редактирование клиентов/исполнителей. Узкое подмножество refs.write
  // (которое также открывает валюты и USDT-курсы) — для ролей, которым нужны только
  // чаты. Удаление аккаунтов остаётся под refs.write (админ).
  'accounts.manage',
  'users.manage',
  // Telegram-listener: правила, бот, шаблоны, события-просмотр — полный CRUD.
  'telegram.manage',
  // Telegram-listener: только управление слушаемыми чатами (CRUD /chats).
  // Узкое подмножество telegram.manage — для ролей, которым нужны чаты, но не правила.
  'telegram.manage-chats',
  // Telegram-listener: применение/отклонение pending events (action-specific
  // право проверяется дополнительно в applyEvent в зависимости от action_type).
  'telegram.apply-events',
  // Балансы по чатам: настройка внешнего источника реальных остатков
  // (URL + Bearer-токен + вкл) на странице /balances. Только admin — токен
  // даёт доступ к внешнему API. Просмотр самих остатков прав не требует (requireAuth).
  'chat-balances.manage',
  // Аудит балансов: просмотр сверки, ручной снапшот, история дельт.
  'audit.view',
  // Аудит балансов: пометить расхождение объяснённым / снять отметку.
  'audit.ack',
  // Публичный API приёма заявок: управление API-ключами, их scope/webhook-настройками,
  // просмотр журналов запросов и доставки вебхуков (страница /admin/integrations).
  // Только admin — ключи дают внешним системам право создавать заявки.
  'integrations.manage',
] as const;
export const RightSchema = z.enum(RIGHTS);
export type Right = z.infer<typeof RightSchema>;

export const RIGHTS_BY_ROLE: Record<RoleCode, readonly Right[]> = {
  admin: [
    'orders.read',
    'orders.create',
    'orders.reject',
    'orders.confirm',
    'orders.edit-provodka',
    'orders.change-executor',
    'orders.rollback-confirm',
    'orders.delete',
    'refs.read',
    'refs.write',
    'accounts.manage',
    'users.manage',
    'telegram.manage',
    'telegram.manage-chats',
    'telegram.apply-events',
    'chat-balances.manage',
    'audit.view',
    'audit.ack',
    'integrations.manage',
  ],
  manager: [
    'orders.read',
    'orders.create',
    'orders.reject',
    'orders.confirm',
    'orders.edit-provodka',
    'orders.change-executor',
    'refs.read',
    // Управляет контрагентами-чатами (/admin/chats), но НЕ листенер-чатами Telegram.
    'accounts.manage',
    'telegram.apply-events',
    'audit.view',
    'audit.ack',
  ],
  operator: [
    'orders.read',
    'orders.create',
    'orders.reject',
    'orders.confirm',
    'orders.change-executor',
    'refs.read',
    'accounts.manage',
    'telegram.apply-events',
  ],
};

export function rightsForRoles(roles: RoleCode[]): Right[] {
  const set = new Set<Right>();
  for (const r of roles) for (const right of RIGHTS_BY_ROLE[r]) set.add(right);
  return [...set];
}

export const LoginInput = z.object({
  email: z.string().email(),
  password: z.string().min(1).max(256),
});
export type LoginInput = z.infer<typeof LoginInput>;

export const RefreshInput = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshInput = z.infer<typeof RefreshInput>;

export const TelegramLoginInput = z.object({
  initData: z.string().min(1),
});
export type TelegramLoginInput = z.infer<typeof TelegramLoginInput>;

export const PublicUser = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string(),
  isActive: z.boolean(),
  telegramUsername: z.string().nullable(),
  roles: z.array(RoleCode),
});
export type PublicUser = z.infer<typeof PublicUser>;

export const AuthTokens = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  user: PublicUser,
});
export type AuthTokens = z.infer<typeof AuthTokens>;

// Ответ /auth/login: либо сразу токены, либо требование TOTP-кода.
// mfaToken — короткоживущий (5 мин) JWT, привязанный к шагу 2FA;
// как access-token не работает (requireAuth его отвергает).
export const LoginResponse = z.object({
  mfaRequired: z.boolean(),
  mfaToken: z.string().nullable(),
  tokens: AuthTokens.nullable(),
});
export type LoginResponse = z.infer<typeof LoginResponse>;

export const TotpLoginInput = z.object({
  mfaToken: z.string().min(1),
  code: z.string().regex(/^\d{6}$/, 'код — 6 цифр'),
});
export type TotpLoginInput = z.infer<typeof TotpLoginInput>;

export const MeResponse = z.object({
  user: PublicUser,
  rights: z.array(RightSchema),
});
export type MeResponse = z.infer<typeof MeResponse>;

// ─── Безопасность аккаунта: пароль, TOTP, passkeys ───

export const ChangePasswordInput = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: z.string().min(8).max(256),
});
export type ChangePasswordInput = z.infer<typeof ChangePasswordInput>;

export const TotpSetupResponse = z.object({
  secret: z.string(),       // base32 — для ручного ввода
  otpauthUri: z.string(),   // для QR-кода
});
export type TotpSetupResponse = z.infer<typeof TotpSetupResponse>;

export const TotpConfirmInput = z.object({
  code: z.string().regex(/^\d{6}$/),
});
export type TotpConfirmInput = z.infer<typeof TotpConfirmInput>;

export const TotpDisableInput = z.object({
  password: z.string().min(1).max(256),
});
export type TotpDisableInput = z.infer<typeof TotpDisableInput>;

export const PasskeyDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type PasskeyDto = z.infer<typeof PasskeyDto>;

export const SecurityOverview = z.object({
  totpEnabled: z.boolean(),
  passkeys: z.array(PasskeyDto),
});
export type SecurityOverview = z.infer<typeof SecurityOverview>;

export const PasskeyRegisterVerifyInput = z.object({
  // Ответ navigator.credentials.create() — структуру валидирует
  // @simplewebauthn/server, здесь passthrough.
  credential: z.record(z.string(), z.unknown()),
  name: z.string().min(1).max(64).default('Passkey'),
});
export type PasskeyRegisterVerifyInput = z.infer<typeof PasskeyRegisterVerifyInput>;

export const PasskeyLoginVerifyInput = z.object({
  challengeId: z.string().min(1),
  credential: z.record(z.string(), z.unknown()),
});
export type PasskeyLoginVerifyInput = z.infer<typeof PasskeyLoginVerifyInput>;
